export const IELTS_PRACTICE_TYPE = 'ielts_speaking';

export const IELTS_PARTS = Object.freeze(['1', '2', '3']);
export const IELTS_MIN_DURATION_SECONDS = 15;
export const IELTS_MAX_DURATION_SECONDS = 300;
export const IELTS_DEFAULT_DURATION_SECONDS = 90;
export const IELTS_DEFAULT_TARGET_BAND = 6;
export const IELTS_AUTHENTIC_FORMAT_VERSION = 2;
export const IELTS_MIC_START_WINDOW_SECONDS = 10;
export const IELTS_PART1_ANSWER_SECONDS = 60;
export const IELTS_PART2_PREPARATION_SECONDS = 60;
export const IELTS_PART2_ANSWER_SECONDS = 120;
export const IELTS_PART3_ANSWER_SECONDS = 60;
export const IELTS_FOLLOWUP_MODES = Object.freeze(['teacher_all', 'random_two', 'adaptive']);

export const IELTS_DIMENSIONS = Object.freeze([
  'fluency_and_coherence',
  'lexical_resource',
  'grammatical_range_and_accuracy',
  'pronunciation',
]);

export const IELTS_DIMENSION_LABELS = Object.freeze({
  fluency_and_coherence: 'Fluency and coherence',
  lexical_resource: 'Lexical resource',
  grammatical_range_and_accuracy: 'Grammatical range and accuracy',
  pronunciation: 'Pronunciation',
});

export const IELTS_RECORDING_STATES = Object.freeze([
  'idle',
  'requesting_microphone',
  'recording',
  'soft_timeout',
  'strict_timeout',
  'uploading',
  'fresh_grading',
  'coaching',
  'result',
  'retryable_failure',
]);

const numberOr = (value, fallback) => {
  if (value == null || (typeof value === 'string' && !value.trim())) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const createIeltsRequestId = (scope = 'request') => {
  const prefix = String(scope || 'request').replace(/[^a-z0-9_-]/gi, '-').slice(0, 40);
  const random = globalThis.crypto?.randomUUID?.()
    || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `ielts-${prefix}-${random}`;
};

const trimOrEmpty = (value) => String(value ?? '').trim();

export const normalizeIeltsQuestionList = (value, maximum = 12) => {
  const source = Array.isArray(value) ? value : trimOrEmpty(value).split(/\r?\n/);
  return source.map(trimOrEmpty).filter(Boolean).slice(0, maximum);
};

const normalizeCueCard = (value = {}) => ({
  topic: trimOrEmpty(value?.topic ?? value?.question).slice(0, 2000),
  bullet_points: normalizeIeltsQuestionList(value?.bullet_points ?? value?.points, 6),
});

const stableIndex = (seed, length) => {
  if (!length) return 0;
  const hash = Array.from(String(seed || 'ielts')).reduce(
    (total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0,
    2166136261,
  );
  return hash % length;
};

export const isAuthenticIeltsConfig = (value = {}) => Number(
  value?.format_version ?? value?.config?.format_version,
) >= IELTS_AUTHENTIC_FORMAT_VERSION;

export const buildAuthenticIeltsTurnPlan = (value = {}, seed = '') => {
  const config = normalizeIeltsConfig(value);
  if (!isAuthenticIeltsConfig(config)) return [];
  if (config.simulation_mode === 'custom') {
    if (!config.question) return [];
    return [{
      id: 'custom-1',
      part: config.part || '',
      question: config.question,
      preparation_seconds: 0,
      answer_limit_seconds: config.suggested_duration_seconds,
    }];
  }
  if (config.simulation_mode === 'part1') {
    return config.part1_questions.map((question, index) => ({
      id: `part1-${index + 1}`,
      part: '1',
      question,
      preparation_seconds: 0,
      answer_limit_seconds: IELTS_PART1_ANSWER_SECONDS,
    }));
  }
  const turns = [];
  if (config.part2_cue_card.topic) {
    turns.push({
      id: 'part2-main',
      part: '2',
      question: config.part2_cue_card.topic,
      bullet_points: config.part2_cue_card.bullet_points,
      preparation_seconds: IELTS_PART2_PREPARATION_SECONDS,
      answer_limit_seconds: IELTS_PART2_ANSWER_SECONDS,
    });
  }
  config.part2_short_followups.forEach((question, index) => turns.push({
    id: `part2-short-${index + 1}`,
    part: '2',
    question,
    preparation_seconds: 0,
    answer_limit_seconds: 30,
  }));
  let followups = config.part3_questions;
  if (config.followup_mode === 'random_two' && followups.length > 2) {
    const first = stableIndex(`${seed}:first`, followups.length);
    let second = stableIndex(`${seed}:second`, followups.length - 1);
    if (second >= first) second += 1;
    followups = [followups[first], followups[second]];
  }
  // Automatic examiner follow-ups are appended by the simulation after a
  // real answer is recorded.  Never consume a teacher seed or manufacture a
  // client-side template here.  ``part3_questions`` only contains questions
  // already generated and persisted for a resumed attempt.
  if (config.followup_mode === 'adaptive') followups = config.part3_questions.slice(0, 2);
  followups.forEach((question, index) => turns.push({
    id: config.followup_mode === 'adaptive' ? `part3-ai-${index + 1}` : `part3-${index + 1}`,
    part: '3',
    question,
    preparation_seconds: 0,
    answer_limit_seconds: IELTS_PART3_ANSWER_SECONDS,
    ai_followup_index: config.followup_mode === 'adaptive' ? index + 1 : null,
  }));
  return turns;
};

export const normalizeIeltsPart = (value) => {
  const part = trimOrEmpty(value);
  return IELTS_PARTS.includes(part) ? part : '';
};

export const normalizeIeltsDuration = (value, fallback = IELTS_DEFAULT_DURATION_SECONDS) => {
  const parsed = Math.round(numberOr(value, fallback));
  return Math.min(
    IELTS_MAX_DURATION_SECONDS,
    Math.max(IELTS_MIN_DURATION_SECONDS, parsed || IELTS_DEFAULT_DURATION_SECONDS),
  );
};

export const getIeltsAssessmentScope = (value = {}) => {
  const question = trimOrEmpty(value.question);
  const part = normalizeIeltsPart(value.part);
  return question || part ? 'prompt' : 'general';
};

export const normalizeIeltsConfig = (value = {}) => {
  const source = value?.config && typeof value.config === 'object'
    ? { ...value, ...value.config }
    : value || {};
  const question = trimOrEmpty(source.question).slice(0, 2000);
  const part = normalizeIeltsPart(source.part);
  const formatVersion = Number(source.format_version) >= IELTS_AUTHENTIC_FORMAT_VERSION
    ? IELTS_AUTHENTIC_FORMAT_VERSION
    : 1;
  const simulationMode = ['part1', 'part2_3', 'custom'].includes(source.simulation_mode)
    ? source.simulation_mode
    : 'part1';
  const followupMode = IELTS_FOLLOWUP_MODES.includes(source.followup_mode)
    ? source.followup_mode
    : 'teacher_all';
  const part1Questions = normalizeIeltsQuestionList(source.part1_questions, 12);
  const part2CueCard = normalizeCueCard(source.part2_cue_card);
  const part2ShortFollowups = normalizeIeltsQuestionList(source.part2_short_followups, 4);
  const generatedPart3Questions = normalizeIeltsQuestionList(source.generated_part3_questions, 2);
  const part3Questions = followupMode === 'adaptive'
    ? generatedPart3Questions
    : normalizeIeltsQuestionList(source.part3_questions, 12);
  const audioIncludesExaminerTts = formatVersion >= IELTS_AUTHENTIC_FORMAT_VERSION
    && (source.audio_includes_examiner_tts == null
      || !['false', '0', 'no'].includes(String(source.audio_includes_examiner_tts).trim().toLowerCase()));
  return {
    question,
    part,
    suggested_duration_seconds: normalizeIeltsDuration(
      source.suggested_duration_seconds ?? source.duration_seconds,
    ),
    strict_timer: formatVersion >= IELTS_AUTHENTIC_FORMAT_VERSION ? true : Boolean(source.strict_timer),
    assessment_scope: getIeltsAssessmentScope({ question, part }),
    target_band: normalizeIeltsBand(source.target_band),
    format_version: formatVersion,
    simulation_mode: simulationMode,
    part1_questions: part1Questions,
    part2_cue_card: part2CueCard,
    part2_short_followups: part2ShortFollowups,
    part3_questions: part3Questions,
    generated_part3_questions: generatedPart3Questions,
    followup_mode: followupMode,
    mic_start_window_seconds: IELTS_MIC_START_WINDOW_SECONDS,
    preparation_seconds: simulationMode === 'part2_3' ? IELTS_PART2_PREPARATION_SECONDS : 0,
    audio_includes_examiner_tts: audioIncludesExaminerTts,
  };
};

export const serializeIeltsSpeakingConfig = (value = {}) => {
  const config = normalizeIeltsConfig(value);
  const serialized = {
    question: config.question || null,
    part: config.part || null,
    suggested_duration_seconds: config.suggested_duration_seconds,
    strict_timer: config.strict_timer,
    assessment_scope: config.assessment_scope,
    target_band: config.target_band,
  };
  if (config.format_version >= IELTS_AUTHENTIC_FORMAT_VERSION) {
    Object.assign(serialized, {
      format_version: IELTS_AUTHENTIC_FORMAT_VERSION,
      simulation_mode: config.simulation_mode,
      part1_questions: config.part1_questions,
      part2_cue_card: config.part2_cue_card,
      part2_short_followups: config.part2_short_followups,
      part3_questions: config.part3_questions,
      generated_part3_questions: config.generated_part3_questions,
      followup_mode: config.followup_mode,
      mic_start_window_seconds: IELTS_MIC_START_WINDOW_SECONDS,
      preparation_seconds: config.preparation_seconds,
      audio_includes_examiner_tts: config.audio_includes_examiner_tts,
    });
  }
  return serialized;
};

export const buildIeltsPracticeOption = (value = {}) => ({
  practice_type: IELTS_PRACTICE_TYPE,
  config: serializeIeltsSpeakingConfig(value),
});

export const getIeltsPromptText = (config = {}) => {
  const normalized = normalizeIeltsConfig(config);
  if (isAuthenticIeltsConfig(normalized)) {
    if (normalized.simulation_mode === 'part1') return normalized.part1_questions[0] || '';
    if (normalized.simulation_mode === 'custom') return normalized.question;
    return normalized.part2_cue_card.topic;
  }
  if (normalized.assessment_scope === 'general') return '';
  return normalized.question;
};

export const getIeltsThreadKey = (value = {}) => {
  const config = normalizeIeltsConfig(value);
  if (isAuthenticIeltsConfig(config)) {
    return `v2::${config.simulation_mode}::${JSON.stringify(serializeIeltsSpeakingConfig(config))}`;
  }
  return `${config.question.toLocaleLowerCase()}::${config.part || 'none'}`;
};

export const formatIeltsBand = (value, fallback = '') => {
  const number = numberOr(value, null);
  if (number == null) return fallback;
  const bounded = Math.min(9, Math.max(0, Math.round(number * 2) / 2));
  return bounded.toFixed(1).replace(/\.0$/, '');
};

export const normalizeIeltsBand = (value) => {
  const number = numberOr(value, null);
  if (number == null) return null;
  return Math.min(9, Math.max(0, Math.round(number * 2) / 2));
};

const readDimension = (source, key) => {
  const aliases = {
    fluency_and_coherence: ['fluency_and_coherence', 'fluency', 'coherence'],
    lexical_resource: ['lexical_resource', 'lexical', 'vocabulary'],
    grammatical_range_and_accuracy: ['grammatical_range_and_accuracy', 'grammar', 'grammatical'],
    pronunciation: ['pronunciation'],
  }[key] || [key];
  return normalizeIeltsBand(aliases.map((alias) => source?.[alias]).find((item) => item != null));
};

const averageBand = (dimensions) => {
  const values = IELTS_DIMENSIONS.map((key) => dimensions[key]).filter((value) => value != null);
  if (values.length !== IELTS_DIMENSIONS.length) return null;
  return Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 2) / 2;
};

export const normalizeIeltsAssessment = (payload = {}) => {
  const attempt = payload?.attempt && typeof payload.attempt === 'object' ? payload.attempt : {};
  const result = payload?.result && typeof payload.result === 'object' ? payload.result : {};
  const grader = payload?.grader && typeof payload.grader === 'object' ? payload.grader : {};
  const details = payload?.details && typeof payload.details === 'object' ? payload.details : {};
  const root = {
    ...(payload && typeof payload === 'object' ? payload : {}),
    ...attempt,
    ...result,
    ...grader,
    ...details,
    ...(attempt.assessment && typeof attempt.assessment === 'object' ? attempt.assessment : {}),
    ...(result.assessment && typeof result.assessment === 'object' ? result.assessment : {}),
    ...(details.assessment && typeof details.assessment === 'object'
      ? details.assessment
      : {}),
    ...(payload?.assessment && typeof payload.assessment === 'object' ? payload.assessment : {}),
  };
  const dimensionsSource = root.dimension_scores || root.dimensions || root.scores || root.band_scores || root;
  const dimensions = IELTS_DIMENSIONS.reduce((result, key) => {
    result[key] = readDimension(dimensionsSource, key);
    return result;
  }, {});
  const overall = normalizeIeltsBand(
    root.predicted_overall
      ?? root.predicted_band
      ?? root.overall_predicted
      ?? root.overall
      ?? root.overall_band
      ?? attempt.metrics?.predicted_overall
      ?? attempt.metrics?.predicted_band
      ?? averageBand(dimensions),
  );
  // A normalized assessment stores coach fields at the top level.  Falling
  // back to root keeps this normalizer idempotent when a result adapter
  // receives an already-normalized authentic-simulation result.
  const coach = root.coach_feedback || root.coach || payload?.coaching || details.coach || root;
  const recording = payload?.recording || root.recording || {};
  const simulationSource = root.simulation_config
    || root.simulation_snapshot
    || root.simulation
    || attempt.simulation_config
    || {};
  const simulation = simulationSource && typeof simulationSource === 'object'
    ? normalizeIeltsConfig(simulationSource)
    : null;
  return {
    predicted_overall: overall,
    dimensions,
    rationale: trimOrEmpty(root.rationale || root.grader_rationale || root.reasoning),
    progress: trimOrEmpty(coach.progress || coach.improvement || coach.what_changed),
    priority_issue: trimOrEmpty(coach.priority_issue || coach.main_issue || coach.focus),
    next_steps: Array.isArray(coach.next_steps)
      ? coach.next_steps.map(trimOrEmpty).filter(Boolean).slice(0, 2)
      : [],
    exercise_action: trimOrEmpty(coach.exercise_action || coach.practice_action || coach.action),
    recording_url: trimOrEmpty(
      payload.recording_url
        || payload.playback_url
        || recording.playback_url
        || recording.url
        || root.recording_url,
    ),
    recording_id: trimOrEmpty(payload.recording_id || recording.id || root.recording_id),
    thread_id: trimOrEmpty(root.thread_id || payload.thread_id),
    attempt_id: trimOrEmpty(root.attempt_id || root.id || payload.attempt_id),
    status: trimOrEmpty(root.status || payload.status),
    created_at: payload.created_at || root.created_at || '',
    simulation_config: simulation,
    simulation_snapshot: simulation,
    generated_part3_questions: simulation?.generated_part3_questions || [],
    audio_includes_examiner_tts: Boolean(
      root.audio_includes_examiner_tts
        ?? simulation?.audio_includes_examiner_tts,
    ),
  };
};

export const normalizeIeltsHistory = (payload = {}) => {
  const values = Array.isArray(payload)
    ? payload
    : payload.attempts || payload.history || payload.items || payload.results || [];
  return values.map((item) => ({
    ...item,
    ...normalizeIeltsAssessment(item),
    question: trimOrEmpty(item.question || item.config?.question),
    part: normalizeIeltsPart(item.part || item.config?.part),
    duration_ms: numberOr(item.duration_ms || item.duration, 0),
  }));
};

export const ieltsBandFromAttempt = (attempt = {}) => normalizeIeltsBand(
  attempt.predicted_overall
    ?? attempt.predicted_band
    ?? attempt.overall_predicted
    ?? attempt.metrics?.predicted_overall
    ?? attempt.metrics?.predicted_band
    ?? attempt.metrics?.overall_predicted
    ?? attempt.score,
);

export const ieltsHistoryDelta = (history = []) => {
  const values = normalizeIeltsHistory(history)
    .map((item) => item.predicted_overall)
    .filter((value) => value != null);
  if (values.length < 2) return null;
  return Math.round((values[0] - values[values.length - 1]) * 2) / 2;
};

export const createIeltsTimerState = ({ mode = 'soft', limitSeconds = IELTS_DEFAULT_DURATION_SECONDS } = {}) => ({
  mode: mode === 'strict' ? 'strict' : 'soft',
  limit_ms: normalizeIeltsDuration(limitSeconds) * 1000,
  elapsed_ms: 0,
  remaining_ms: normalizeIeltsDuration(limitSeconds) * 1000,
  timed_out: false,
  allow_overtime: false,
  status: 'ready',
});

export const tickIeltsTimer = (timer, elapsedMs) => {
  const current = timer || createIeltsTimerState();
  const elapsed = Math.max(0, Math.round(numberOr(elapsedMs, current.elapsed_ms)));
  const remaining = Math.max(0, current.limit_ms - elapsed);
  const timedOut = current.timed_out || elapsed >= current.limit_ms;
  return {
    ...current,
    elapsed_ms: elapsed,
    remaining_ms: remaining,
    timed_out: timedOut,
    status: current.mode === 'strict'
      ? (timedOut ? 'strict_timeout' : 'recording')
      : (timedOut && !current.allow_overtime ? 'soft_timeout' : 'recording'),
  };
};

export const formatIeltsTimer = (milliseconds) => {
  const seconds = Math.max(0, Math.ceil(numberOr(milliseconds, 0) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

export const transitionIeltsRecordingState = (state = 'idle', event) => {
  const current = IELTS_RECORDING_STATES.includes(state) ? state : 'idle';
  const transitions = {
    REQUEST_MICROPHONE: { idle: 'requesting_microphone', result: 'requesting_microphone', retryable_failure: 'requesting_microphone' },
    MICROPHONE_GRANTED: { requesting_microphone: 'recording' },
    SOFT_TIMEOUT: { recording: 'soft_timeout' },
    CONTINUE_RECORDING: { soft_timeout: 'recording' },
    STRICT_TIMEOUT: { recording: 'strict_timeout', soft_timeout: 'strict_timeout' },
    STOP_RECORDING: { recording: 'uploading', soft_timeout: 'uploading', strict_timeout: 'uploading' },
    GRADING_STARTED: { uploading: 'fresh_grading' },
    COACHING_STARTED: { fresh_grading: 'coaching' },
    RESULT_READY: { coaching: 'result', fresh_grading: 'result' },
    RETRYABLE_FAILURE: {
      requesting_microphone: 'retryable_failure',
      recording: 'retryable_failure',
      uploading: 'retryable_failure',
      fresh_grading: 'retryable_failure',
      coaching: 'retryable_failure',
    },
    RETRY_RECORDING: { retryable_failure: 'uploading' },
    NEW_THREAD: { result: 'idle', retryable_failure: 'idle' },
  };
  return transitions[event]?.[current] || current;
};

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const buildIeltsRequestError = (payload = {}, status = 0) => {
  const structuredError = payload?.error && typeof payload.error === 'object'
    ? payload.error
    : {};
  const message = trimOrEmpty(
    payload?.message
      || (typeof payload?.error === 'string' ? payload.error : '')
      || structuredError.message
      || structuredError.detail,
  ) || 'The IELTS speaking request failed.';
  const error = new Error(message);
  error.status = status;
  error.code = payload.code || structuredError.code;
  error.payload = payload;
  return error;
};

export const getIeltsUploadProgress = ({ loaded = 0, total = 0, complete = false } = {}) => {
  if (complete) return 67;
  const safeTotal = Math.max(0, numberOr(total, 0));
  if (!safeTotal) return 0;
  const ratio = Math.min(1, Math.max(0, numberOr(loaded, 0) / safeTotal));
  return Math.min(67, Math.round(ratio * 67));
};

const uploadIeltsFormJson = (url, body, onUploadProgress) => {
  if (typeof onUploadProgress !== 'function' || typeof XMLHttpRequest === 'undefined') {
    return fetchIeltsJson(url, { method: 'POST', body });
  }
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', url);
    const token = localStorage.getItem('token');
    if (token) request.setRequestHeader('Authorization', `Bearer ${token}`);
    request.setRequestHeader('Accept-Language', localStorage.getItem('lexilab.locale') || 'en');
    request.responseType = 'json';
    request.upload.onprogress = (event) => onUploadProgress({
      loaded: event.loaded,
      total: event.lengthComputable ? event.total : 0,
      complete: false,
    });
    request.upload.onload = () => onUploadProgress({ loaded: 1, total: 1, complete: true });
    request.onerror = () => reject(buildIeltsRequestError({}, request.status));
    request.onload = () => {
      let payload = request.response;
      if (!payload || typeof payload !== 'object') {
        try {
          payload = request.responseText ? JSON.parse(request.responseText) : {};
        } catch (_) {
          payload = {};
        }
      }
      if (request.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('lexilamp:auth-expired'));
      }
      if (request.status < 200 || request.status >= 300 || payload?.ok === false) {
        reject(buildIeltsRequestError(payload, request.status));
        return;
      }
      resolve(payload);
    };
    request.send(body);
  });
};

export const fetchIeltsJson = async (url, options = {}) => {
  const hasFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const response = await fetch(url, {
    ...options,
    // The browser must supply FormData's multipart boundary. Marking an audio
    // upload as JSON makes Flask receive neither request.files nor request.form.
    headers: { ...authHeaders(Boolean(options.body) && !hasFormData), ...(options.headers || {}) },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    throw buildIeltsRequestError(payload, response.status);
  }
  return payload;
};

export const createIeltsSpeakingThread = (config, requestId = createIeltsRequestId('thread')) => fetchIeltsJson(
  '/api/student/ielts-speaking/threads',
  {
    method: 'POST',
    body: JSON.stringify({
      ...serializeIeltsSpeakingConfig(config),
      new_thread: true,
      idempotency_key: requestId,
    }),
  },
);

export const startIeltsSpeakingAttempt = (threadId, requestId = createIeltsRequestId('attempt')) => fetchIeltsJson(
  `/api/student/ielts-speaking/threads/${encodeURIComponent(threadId)}/attempts`,
  {
    method: 'POST',
    body: JSON.stringify({ idempotency_key: requestId }),
  },
);

export const startAssignedIeltsSpeakingAttempt = (
  assignmentId,
  requestId = createIeltsRequestId('assigned-session'),
) => fetchIeltsJson(
  `/api/student/ielts-speaking/assignments/${encodeURIComponent(assignmentId)}/start`,
  {
    method: 'POST',
    body: JSON.stringify({ idempotency_key: requestId }),
  },
);

export const fetchIeltsSpeakingThread = (threadId) => fetchIeltsJson(
  `/api/student/ielts-speaking/threads/${encodeURIComponent(threadId)}`,
).then((payload) => payload.thread || payload);

export const fetchIeltsSpeakingThreads = ({ assignmentId = '', limit = 100, includeAttempts = false } = {}) => {
  const query = new URLSearchParams({ limit: String(Math.min(100, Math.max(1, limit))) });
  if (assignmentId) query.set('assignment_id', assignmentId);
  if (includeAttempts) query.set('include_attempts', '1');
  return fetchIeltsJson(`/api/student/ielts-speaking/threads?${query.toString()}`)
    .then((payload) => (Array.isArray(payload.threads) ? payload.threads : []));
};

export const buildIeltsRecordingUrl = ({ recordingId, viewer = 'student', studentId = '' } = {}) => {
  const id = trimOrEmpty(recordingId);
  if (!id) return '';
  if (viewer === 'admin') {
    const owner = trimOrEmpty(studentId);
    if (!owner) return '';
    return `/api/admin/students/${encodeURIComponent(owner)}/ielts-speaking/recordings/${encodeURIComponent(id)}`;
  }
  return `/api/student/ielts-speaking/recordings/${encodeURIComponent(id)}`;
};

export const fetchIeltsSpeakingHistory = ({ threadId = '', limit = 50 } = {}) => {
  const query = new URLSearchParams({ limit: String(Math.min(100, Math.max(1, limit))) });
  const primaryUrl = threadId
    ? `/api/student/ielts-speaking/threads/${encodeURIComponent(threadId)}/attempts?${query.toString()}`
    : `/api/student/ielts-speaking/history?${query.toString()}`;
  const fallbackUrl = threadId
    ? `/api/student/ielts-speaking/history?${query.toString()}&thread_id=${encodeURIComponent(threadId)}`
    : '';
  return fetchIeltsJson(primaryUrl)
    .catch((error) => {
      if (!fallbackUrl || error.status !== 404) throw error;
      return fetchIeltsJson(fallbackUrl);
    })
    .then((payload) => normalizeIeltsHistory(payload));
};

export const uploadIeltsSpeakingRecording = async ({
  blob,
  config,
  threadId,
  assignmentId,
  targetId,
  attemptId,
  durationMs,
  stoppedBy,
  requestId = createIeltsRequestId('recording'),
  onUploadProgress,
}) => {
  const serialized = serializeIeltsSpeakingConfig(config);
  const buildBody = ({ includeAttempt = true } = {}) => {
    const body = new FormData();
    body.append('audio', blob, `ielts-speaking.${blob?.type?.includes('mp4') ? 'm4a' : 'webm'}`);
    Object.entries(serialized).forEach(([key, value]) => {
      if (value != null) body.append(key, String(value));
    });
    if (threadId) body.append('thread_id', threadId);
    if (assignmentId) body.append('assignment_id', assignmentId);
    if (targetId) body.append('target_id', targetId);
    if (includeAttempt && attemptId) body.append('attempt_id', attemptId);
    body.append('idempotency_key', requestId);
    body.append('duration_ms', String(Math.max(0, Math.round(numberOr(durationMs, 0)))));
    if (stoppedBy) body.append('stopped_by', stoppedBy);
    body.append('stopped_by_timer', stoppedBy === 'strict_timer' ? 'true' : 'false');
    return body;
  };
  const primaryUrl = attemptId
    ? `/api/student/ielts-speaking/attempts/${encodeURIComponent(attemptId)}/submit`
    : threadId
      ? `/api/student/ielts-speaking/threads/${encodeURIComponent(threadId)}/attempts`
      : '/api/student/ielts-speaking/attempts';
  const fallbackUrl = attemptId && threadId
    ? `/api/student/ielts-speaking/threads/${encodeURIComponent(threadId)}/attempts`
    : '';
  return uploadIeltsFormJson(primaryUrl, buildBody(), onUploadProgress)
    .catch((error) => {
      const staleAttempt = error.status === 404
        || error.code === 'ielts_speaking.attempt_thread_mismatch';
      if (!fallbackUrl || !staleAttempt) throw error;
      // A stale attempt must not strand a locally recorded answer. Create one
      // replacement attempt in the same thread and submit the same audio.
      return uploadIeltsFormJson(
        fallbackUrl,
        buildBody({ includeAttempt: false }),
        onUploadProgress,
      );
  });
};

export const requestIeltsSpeakingFollowup = ({
  blob,
  attemptId,
  threadId = '',
  turnId,
  question,
  part,
  cueCard = {},
  askedQuestions = [],
  transcript = '',
  requestId = createIeltsRequestId('followup'),
}) => {
  const id = trimOrEmpty(attemptId);
  if (!id) return Promise.reject(new Error('The IELTS speaking attempt was not found.'));
  if (!blob?.size) return Promise.reject(new Error('No answer was recorded for the follow-up.'));
  const body = new FormData();
  const mimeType = blob.type || 'audio/webm';
  body.append('audio', blob, `ielts-followup.${mimeType.includes('mp4') ? 'm4a' : 'webm'}`);
  body.append('mime_type', mimeType);
  body.append('attempt_id', id);
  if (threadId) body.append('thread_id', threadId);
  body.append('turn_id', trimOrEmpty(turnId));
  body.append('current_question', trimOrEmpty(question));
  body.append('current_part', trimOrEmpty(part));
  body.append('cue_card', JSON.stringify(cueCard || {}));
  body.append('asked_questions', JSON.stringify(Array.isArray(askedQuestions) ? askedQuestions : []));
  if (transcript) body.append('transcript', trimOrEmpty(transcript));
  body.append('idempotency_key', requestId);
  return uploadIeltsFormJson(
    `/api/student/ielts-speaking/attempts/${encodeURIComponent(id)}/followup`,
    body,
  );
};

export const retryIeltsSpeakingScoring = ({ attemptId, threadId = '' } = {}) => {
  const id = trimOrEmpty(attemptId);
  if (!id) return Promise.reject(new Error('The IELTS speaking attempt was not found.'));
  return fetchIeltsJson(
    `/api/student/ielts-speaking/attempts/${encodeURIComponent(id)}/submit`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(threadId ? { thread_id: threadId } : {}),
        idempotency_key: createIeltsRequestId('scoring-retry'),
      }),
    },
  );
};

export const fetchIeltsSpeakingAttempt = (attemptId) => fetchIeltsJson(
  `/api/student/ielts-speaking/attempts/${encodeURIComponent(attemptId)}`,
).then((payload) => ({
  ...payload,
  ...(payload?.attempt && typeof payload.attempt === 'object' ? payload.attempt : {}),
  ...normalizeIeltsAssessment(payload),
}));

export const buildIeltsSpeakingLaunchUrl = ({ payload = {}, mode = '', route } = {}) => {
  const params = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (value != null && key !== 'items') params.set(key, String(value));
  });
  if (mode) params.set('mode', mode);
  const query = params.toString();
  return `${route || '/student/self-check/ielts-speaking'}${query ? `?${query}` : ''}`;
};

export const getIeltsResultActions = ({ hasResult, hasThread, hasRecording } = {}) => ({
  canPracticeAgain: Boolean(hasResult && hasThread),
  canStartNewThread: Boolean(hasResult),
  canPlayRecording: Boolean(hasRecording),
});
